import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Eye, EyeOff } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { logger } from "@/infrastructure/lib/logger";

interface CopyFieldProps {
  label: string;
  value: string;
  /** Segredo: começa escondido, com um olho para mostrar. */
  secret?: boolean;
}

/** Um valor para repassar a quem vai chamar o webhook: endereço, usuário, senha ou token. */
export function CopyField({ label, value, secret = false }: CopyFieldProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(!secret);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      // Sem área de transferência (permissão negada): o valor segue à vista para copiar à mão.
      setVisible(true);
      logger.warn("[CopyField] Clipboard unavailable", { error, label });
    }
  };

  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-1 rounded-md border border-border/60 py-1 pl-2 pr-1">
        <code className="flex-1 break-all text-xs">{visible ? value : "•".repeat(Math.min(value.length, 24))}</code>
        {secret && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0"
            title={t(visible ? "jobs.webhook.hide" : "jobs.webhook.show")}
            aria-label={t(visible ? "jobs.webhook.hide" : "jobs.webhook.show")}
            onClick={() => setVisible((v) => !v)}
          >
            {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          title={t(copied ? "jobs.webhook.copied" : "jobs.webhook.copy")}
          aria-label={t(copied ? "jobs.webhook.copied" : "jobs.webhook.copy")}
          onClick={copy}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}
